import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { X, Send, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_return-dialog/artifacts/cartnx7j_7924d478-99b6-4d36-83f9-c10117d2477f.jpg';

const ChatbotWidget = () => {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const txt = {
    it: {
      greeting: 'Ciao! Sono l\'assistente di Italian Roots Discovery. Come posso aiutarti a scoprire le tue radici italiane?',
      placeholder: 'Scrivi un messaggio...',
      title: 'Assistente IRD',
      subtitle: 'Online',
      quickActions: [
        { label: 'Come funziona la ricerca?', msg: 'Come funziona la ricerca genealogica?' },
        { label: 'Quanto costa?', msg: 'Quanto costa una ricerca genealogica?' },
        { label: 'Cittadinanza italiana', msg: 'Come posso ottenere la cittadinanza italiana?' },
      ],
    },
    en: {
      greeting: 'Hello! I\'m the Italian Roots Discovery assistant. How can I help you discover your Italian roots?',
      placeholder: 'Type a message...',
      title: 'IRD Assistant',
      subtitle: 'Online',
      quickActions: [
        { label: 'How does research work?', msg: 'How does genealogical research work?' },
        { label: 'How much does it cost?', msg: 'How much does genealogical research cost?' },
        { label: 'Italian citizenship', msg: 'How can I get Italian citizenship?' },
      ],
    },
    es: {
      greeting: 'Hola! Soy el asistente de Italian Roots Discovery. Como puedo ayudarte a descubrir tus raices italianas?',
      placeholder: 'Escribe un mensaje...',
      title: 'Asistente IRD',
      subtitle: 'En linea',
      quickActions: [
        { label: 'Como funciona?', msg: 'Como funciona la investigacion genealogica?' },
        { label: 'Cuanto cuesta?', msg: 'Cuanto cuesta una investigacion genealogica?' },
        { label: 'Ciudadania italiana', msg: 'Como puedo obtener la ciudadania italiana?' },
      ],
    },
    pt: {
      greeting: 'Ola! Sou o assistente do Italian Roots Discovery. Como posso ajuda-lo a descobrir suas raizes italianas?',
      placeholder: 'Digite uma mensagem...',
      title: 'Assistente IRD',
      subtitle: 'Online',
      quickActions: [
        { label: 'Como funciona?', msg: 'Como funciona a pesquisa genealogica?' },
        { label: 'Quanto custa?', msg: 'Quanto custa uma pesquisa genealogica?' },
        { label: 'Cidadania italiana', msg: 'Como posso obter a cidadania italiana?' },
      ],
    },
  };
  const t = txt[language] || txt.it;

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: 'assistant', text: t.greeting }]);
    }
  }, [open, t.greeting, messages.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', text: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/chatbot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text.trim(), session_id: sessionId, language }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', text: data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: language === 'it' ? 'Mi scuso, c\'è un problema di connessione. Riprova tra poco!' : 'Sorry, connection issue. Please try again!' }]);
    }
    setLoading(false);
  };

  const renderLinks = (text) => {
    const parts = text.split(/(\/(free-evaluation|booking))/g);
    return parts.map((part, i) => {
      if (part === '/free-evaluation') {
        return <Link key={i} to="/free-evaluation" className="text-amber-700 underline font-medium hover:text-amber-900" onClick={() => setOpen(false)}>{language === 'it' ? 'Valutazione Gratuita' : 'Free Evaluation'}</Link>;
      }
      if (part === '/booking') {
        return <Link key={i} to="/booking" className="text-amber-700 underline font-medium hover:text-amber-900" onClick={() => setOpen(false)}>{language === 'it' ? 'Prenota Consulenza' : 'Book Consultation'}</Link>;
      }
      if (part === 'free-evaluation' || part === 'booking') return null;
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <>
      {/* Toggle Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chatbot-toggle"
          className="fixed bottom-20 right-20 z-50 w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-700 rounded-full shadow-xl flex items-center justify-center hover:scale-110 transition-all overflow-hidden ring-2 ring-amber-300/50"
          style={{ animation: 'pulse 2s ease-in-out infinite' }}
          aria-label="Open chatbot"
        >
          <img src={LOGO_URL} alt="IRD" className="w-12 h-12 object-cover rounded-full" />
        </button>
      )}

      {/* Chat Window */}
      {open && (
        <div
          data-testid="chatbot-window"
          className="fixed bottom-4 right-4 z-50 w-[370px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-amber-100 flex flex-col overflow-hidden"
          style={{ height: '520px' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-amber-600 to-amber-800 text-white px-5 py-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center overflow-hidden ring-2 ring-white/50">
                <img src={LOGO_URL} alt="IRD" className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="font-semibold text-sm" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t.title}</p>
                <p className="text-xs text-amber-100 flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-400 rounded-full inline-block" />
                  {t.subtitle}
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="hover:bg-white/20 rounded-full p-1 transition" data-testid="chatbot-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-amber-50/30" data-testid="chatbot-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-br from-amber-600 to-amber-700 text-white rounded-br-md'
                      : 'bg-white text-gray-800 border border-amber-100 shadow-sm rounded-bl-md'
                  }`}
                >
                  {msg.role === 'assistant' ? renderLinks(msg.text) : msg.text}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-amber-100 shadow-sm px-4 py-3 rounded-2xl rounded-bl-md">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions (only show at start) */}
            {messages.length <= 1 && !loading && (
              <div className="space-y-2 pt-1">
                {t.quickActions.map((qa, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(qa.msg)}
                    className="w-full text-left px-4 py-2.5 bg-white border border-amber-200 rounded-xl text-sm text-amber-800 hover:bg-amber-50 hover:border-amber-400 transition flex items-center justify-between group"
                    data-testid={`chatbot-quick-${i}`}
                  >
                    {qa.label}
                    <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition" />
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-amber-100 bg-white flex-shrink-0">
            <form
              onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
              className="flex items-center gap-2"
            >
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t.placeholder}
                disabled={loading}
                className="flex-1 px-4 py-2.5 bg-amber-50/50 rounded-full text-sm outline-none focus:ring-2 focus:ring-amber-400 focus:bg-white transition disabled:opacity-60"
                data-testid="chatbot-input"
              />
              <button
                type="submit"
                disabled={!input.trim() || loading}
                className="w-10 h-10 bg-gradient-to-br from-amber-600 to-amber-700 text-white rounded-full flex items-center justify-center hover:from-amber-700 hover:to-amber-800 transition disabled:opacity-40"
                data-testid="chatbot-send"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(217, 119, 6, 0.5); }
          50% { box-shadow: 0 0 0 14px rgba(217, 119, 6, 0); }
        }
      `}</style>
    </>
  );
};

export default ChatbotWidget;
